import { Decoration } from '@tiptap/pm/view';
import type { ReviewMode } from '@/lib/ai-composer/types';
import { computeWordDiff } from './diff';

export interface SuggestionSpec {
  id: string;
  from: number;
  to: number;
  text: string;
  mode: ReviewMode;
  streaming?: boolean;
  before?: string;
}

export interface RenderClasses {
  ghostClass: string;
  ghostStreamingClass: string;
  previewAddedClass?: string;
  reviewAddedClass?: string;
  reviewRemovedClass?: string;
}

/**
 * An inline decoration spanning the written range, applying the shimmer class to the
 * REAL streamed text (not a separate widget). This is the "ghost-write" reveal — the
 * text being written shimmers in place. Returns null when nothing is written yet
 * (from === to), since there's no text to style.
 */
/** Positions of trailing text near the write end to highlight (the just-written tail). */
const SHIMMER_TAIL = 20;

function shimmerSpan(spec: SuggestionSpec, classes: RenderClasses): Decoration | null {
  if (spec.to <= spec.from) return null;
  // Only the LAST SHIMMER_TAIL positions shimmer (near the caret), not the whole range —
  // shimmering the entire growing output made the whole document pulse and flicker.
  const start = Math.max(spec.from, spec.to - SHIMMER_TAIL);
  return Decoration.inline(
    start,
    spec.to,
    { class: classes.ghostStreamingClass, 'data-ai-suggestion-id': spec.id, 'data-streaming': 'true' },
    { key: `ai-${spec.id}-shimmer` },
  );
}

/** Invisible inline decoration that maps [from,to] through transactions; carries mode and before. */
function rangeTracker(spec: SuggestionSpec): Decoration {
  return Decoration.inline(spec.from, spec.to, {}, { key: `ai-${spec.id}-range`, mode: spec.mode, before: spec.before });
}

function previewSpan(spec: SuggestionSpec, classes: RenderClasses): Decoration | null {
  if (spec.to <= spec.from || !classes.previewAddedClass) return null;
  return Decoration.inline(
    spec.from,
    spec.to,
    { class: classes.previewAddedClass, 'data-ai-suggestion-id': spec.id },
    { key: `ai-${spec.id}-preview`, class: classes.previewAddedClass },
  );
}

function reviewDecorations(spec: SuggestionSpec, classes: RenderClasses): Decoration[] {
  const out: Decoration[] = [];
  if (spec.to > spec.from && classes.reviewAddedClass) {
    out.push(Decoration.inline(spec.from, spec.to,
      { class: classes.reviewAddedClass, 'data-ai-suggestion-id': spec.id },
      { key: `ai-${spec.id}-review-added`, class: classes.reviewAddedClass }));
  }
  const removedText = computeWordDiff(spec.before ?? '', spec.text ?? '')
    .filter((s) => s.type === 'removed').map((s) => s.text).join('');
  if (removedText && classes.reviewRemovedClass) {
    out.push(Decoration.widget(spec.from, () => {
      const el = document.createElement('span');
      el.className = classes.reviewRemovedClass!;
      el.setAttribute('data-ai-removed', spec.id);
      el.textContent = removedText;
      return el;
    }, { side: -1, key: `ai-${spec.id}-review-removed` }));
  }
  return out;
}

export function renderSuggestionDecorations(spec: SuggestionSpec, classes: RenderClasses): Decoration[] {
  switch (spec.mode) {
    case 'preview': {
      const decos: Decoration[] = [rangeTracker(spec)];
      if (spec.streaming) {
        const shimmer = shimmerSpan(spec, classes);
        if (shimmer) decos.push(shimmer);
      } else {
        const prev = previewSpan(spec, classes);
        if (prev) decos.push(prev);
      }
      return decos;
    }
    case 'review': {
      const decos: Decoration[] = [rangeTracker(spec)];
      if (spec.streaming) {
        const shimmer = shimmerSpan(spec, classes);
        if (shimmer) decos.push(shimmer);
      } else {
        decos.push(...reviewDecorations(spec, classes));
      }
      return decos;
    }
    case 'ghost-write':
    case 'embed': {
      // embed presentation lands in a later task; until then it shares the
      // ghost-write reveal: shimmer the written text in place + track the range.
      const decos: Decoration[] = [rangeTracker(spec)];
      if (spec.streaming) {
        const shimmer = shimmerSpan(spec, classes);
        if (shimmer) decos.push(shimmer);
      }
      return decos;
    }
    case 'disabled':
      return [];
  }
}
